import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "./api/axios";
import { useCart } from "./context/CartContext";
import { useI18n } from "../i18n/I18nContext";

export default function Checkout() {
  const navigate = useNavigate();
  const { t } = useI18n();

  const {
    items = [],
    subtotal = 0,
    subtotal_without_iva = null,
    iva_rate = 0.23,
    total_weight_kg = 0,
    shipping_cost = null,
  } = useCart();

  const formatPostalCode = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 7);
    if (digits.length <= 4) return digits;
    return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  };

  const ivaRate = Number(iva_rate ?? 0.23);
  const totalWeightKg = Number(total_weight_kg ?? 0);

  const [couponCode, setCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponFeedback, setCouponFeedback] = useState("");
  const [couponResult, setCouponResult] = useState(null);

  const [saveAddress, setSaveAddress] = useState(true);
  const [orderCreated, setOrderCreated] = useState(false);

  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [useNewAddress, setUseNewAddress] = useState(false);

  const [shipping, setShipping] = useState({
    line1: "",
    city: "",
    postal_code: "",
    country_code: "PT",
    phone: "",
  });

  const [billingEnabled, setBillingEnabled] = useState(false);
  const [billing, setBilling] = useState({
    line1: "",
    city: "",
    postal_code: "",
    country_code: "PT",
    phone: "",
  });

  const [taxNumber, setTaxNumber] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      return user?.tax_number ? String(user.tax_number) : "";
    } catch {
      return "";
    }
  });

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const subtotalWithIva = Number(subtotal ?? 0);

  const subtotalWithoutIva = Number(
    subtotal_without_iva !== null &&
      subtotal_without_iva !== undefined &&
      Number(subtotal_without_iva) > 0
      ? subtotal_without_iva
      : items.reduce((sum, item) => {
          const qty = Number(item.quantity || 0);

          if (item.line_total_without_iva !== undefined) {
            return sum + Number(item.line_total_without_iva || 0);
          }

          if (item.price_without_iva !== undefined) {
            return sum + Number(item.price_without_iva || 0) * qty;
          }

          if (item.unit_price_without_iva !== undefined) {
            return sum + Number(item.unit_price_without_iva || 0) * qty;
          }

          if (item.line_total !== undefined) {
            return sum + Number(item.line_total || 0) / (1 + ivaRate);
          }

          return sum + (Number(item.price || 0) * qty) / (1 + ivaRate);
        }, 0)
  );

  const discountAmount = Number(couponResult?.discount_amount ?? 0);

  const subtotalAfterDiscountWithoutIva =
    couponResult?.subtotal_after_discount_without_iva !== undefined
      ? Number(couponResult.subtotal_after_discount_without_iva)
      : Math.max(subtotalWithoutIva - discountAmount, 0);

  const subtotalAfterDiscountWithIva =
    couponResult?.subtotal_after_discount_with_iva !== undefined
      ? Number(couponResult.subtotal_after_discount_with_iva)
      : subtotalAfterDiscountWithoutIva * (1 + ivaRate);

  const ivaAmountAfterDiscount =
    subtotalAfterDiscountWithIva - subtotalAfterDiscountWithoutIva;

  const shippingCost =
    couponResult?.shipping_cost !== undefined &&
    couponResult?.shipping_cost !== null
      ? Number(couponResult.shipping_cost)
      : shipping_cost !== null && shipping_cost !== undefined
        ? Number(shipping_cost)
        : null;

  const totalAfterDiscount =
    couponResult?.total_after_discount !== undefined
      ? Number(couponResult.total_after_discount)
      : subtotalAfterDiscountWithIva + Number(shippingCost ?? 0);

  const getFieldError = (name) => fieldErrors?.[name]?.[0] || "";

  const inputClass = (name) =>
    `w-full rounded border px-3 py-2 focus:outline-none focus:ring ${
      getFieldError(name) ? "border-red-500" : "border-gray-300"
    }`;

  const clearFieldError = (name) => {
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleSelectAddress = (addr) => {
    setSelectedAddressId(addr.id);
    setUseNewAddress(false);
    setShipping({
      line1: addr.line1 || "",
      city: addr.city || "",
      postal_code: addr.postal_code || "",
      country_code: addr.country_code || "PT",
      phone: addr.phone || "",
    });

    clearFieldError("shipping.line1");
    clearFieldError("shipping.city");
    clearFieldError("shipping.postal_code");
    clearFieldError("shipping.country_code");
    clearFieldError("shipping.phone");
  };

  const handleUseNewAddress = () => {
    setSelectedAddressId(null);
    setUseNewAddress(true);
    setShipping({
      line1: "",
      city: "",
      postal_code: "",
      country_code: "PT",
      phone: "",
    });
  };

  useEffect(() => {
    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user") || "null");

      if (!token || !user) {
        navigate("/login", { replace: true });
      }
    } catch {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  const onShip = (key) => (e) => {
    setShipping((prev) => ({ ...prev, [key]: e.target.value }));
    clearFieldError(`shipping.${key}`);
  };

  const onBill = (key) => (e) => {
    setBilling((prev) => ({ ...prev, [key]: e.target.value }));
    clearFieldError(`billing.${key}`);
  };

  const validateCoupon = async () => {
    const code = couponCode.trim();

    if (!code) {
      setCouponResult(null);
      setCouponFeedback("");
      return;
    }

    setCouponLoading(true);
    setCouponFeedback("");
    setErrorMsg("");

    try {
      const res = await api.post("/api/v1/coupons/validate", {
        code,
        subtotal: Number(subtotalWithoutIva),
        total_weight_kg: Number(totalWeightKg),
      });

      setCouponResult(res.data);
      setCouponFeedback(res.data?.message || t("Cupão válido."));
    } catch (err) {
      const data = err?.response?.data;
      setCouponResult(null);
      setCouponFeedback(data?.message || t("Cupão inválido."));
    } finally {
      setCouponLoading(false);
    }
  };

  useEffect(() => {
    api
      .get("/api/v1/addresses")
      .then((res) => {
        const addrs = res?.data?.data || [];
        setSavedAddresses(addrs);

        if (addrs.length === 0) {
          setUseNewAddress(true);
        } else {
          handleSelectAddress(addrs[0]);
        }
      })
      .catch(() => setUseNewAddress(true))
      .finally(() => setLoadingAddresses(false));
  }, []);

  useEffect(() => {
    if (!items.length && !orderCreated) {
      navigate("/products", { replace: true });
    }
  }, [items.length, orderCreated, navigate]);

  useEffect(() => {
    setCouponResult(null);
    setCouponFeedback("");
  }, [subtotalWithoutIva, shipping_cost, totalWeightKg]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setFieldErrors({});
    setLoading(true);

    try {
      const cleanTax = (taxNumber || "").replace(/\D/g, "").slice(0, 9);

      const shippingPayload = {
        line1: shipping.line1.trim(),
        city: shipping.city.trim(),
        postal_code: shipping.postal_code.trim(),
        country_code: shipping.country_code.trim().toUpperCase(),
        phone: shipping.phone.trim() || null,
      };

      const billingPayload = {
        ...(billingEnabled
          ? {
              line1: billing.line1.trim(),
              city: billing.city.trim(),
              postal_code: billing.postal_code.trim(),
              country_code: billing.country_code.trim().toUpperCase(),
              phone: billing.phone.trim() || null,
            }
          : shippingPayload),
        tax_number: cleanTax || null,
      };

      const payload = {
        source: "cart",
        coupon_code: couponCode?.trim() || null,
        save_address: useNewAddress ? !!saveAddress : false,
        shipping: shippingPayload,
        billing: billingPayload,
      };

      const res = await api.post("/api/v1/checkout/session", payload);
      const data = res?.data;

      if (!data?.checkout_url) {
        throw new Error("A Stripe não devolveu checkout_url.");
      }

      setOrderCreated(true);
      window.location.href = data.checkout_url;
    } catch (err) {
      const data = err?.response?.data;

      if (data?.errors) {
        setFieldErrors(data.errors);
        setErrorMsg("");
      } else {
        setFieldErrors({});
        setErrorMsg(
          data?.message ||
            err?.message ||
            t("Erro ao iniciar pagamento com Stripe.")
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (!items.length && !orderCreated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 px-6 py-10">
      <div className="mx-auto max-w-5xl">
        <h1 className="mb-6 text-2xl font-bold text-gray-900">
          {t("Checkout")}
        </h1>

        {errorMsg && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {errorMsg}
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              <div>
                <h2 className="mb-3 text-lg font-semibold text-gray-900">
                  {t("Morada de envio")}
                </h2>

                {loadingAddresses ? (
                  <p className="animate-pulse text-sm text-gray-400">
                    {t("A carregar moradas...")}
                  </p>
                ) : (
                  <>
                    {savedAddresses.length > 0 && (
                      <div className="mb-4 space-y-2">
                        <p className="text-sm font-medium text-gray-600">
                          {t("As tuas moradas guardadas:")}
                        </p>

                        {savedAddresses.map((addr) => (
                          <button
                            key={addr.id}
                            type="button"
                            onClick={() => handleSelectAddress(addr)}
                            className={`w-full rounded-lg border px-4 py-3 text-left text-sm transition-all ${
                              selectedAddressId === addr.id && !useNewAddress
                                ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500"
                                : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {addr.label && (
                              <span className="mb-0.5 block text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                {addr.label}
                              </span>
                            )}

                            <div className="font-medium text-gray-900">
                              {addr.line1}
                            </div>

                            <div className="mt-0.5 text-gray-500">
                              {addr.postal_code} {addr.city} ·{" "}
                              {addr.country_code}
                              {addr.phone && ` · ${addr.phone}`}
                            </div>
                          </button>
                        ))}

                        <button
                          type="button"
                          onClick={handleUseNewAddress}
                          className={`w-full rounded-lg border px-4 py-3 text-left text-sm transition-all ${
                            useNewAddress
                              ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500"
                              : "border-dashed border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50"
                          }`}
                        >
                          <div className="font-medium text-gray-700">
                            {t("+ Usar uma nova morada")}
                          </div>
                        </button>
                      </div>
                    )}

                    {(useNewAddress || savedAddresses.length === 0) && (
                      <div className="space-y-4">
                        <div>
                          <label className="mb-1 block text-sm font-medium">
                            {t("Morada (linha 1)")}
                          </label>
                          <input
                            type="text"
                            value={shipping.line1}
                            onChange={onShip("line1")}
                            className={inputClass("shipping.line1")}
                          />
                          {getFieldError("shipping.line1") && (
                            <p className="mt-1 text-sm text-red-600">
                              {getFieldError("shipping.line1")}
                            </p>
                          )}
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                          <div>
                            <label className="mb-1 block text-sm font-medium">
                              {t("Cidade")}
                            </label>
                            <input
                              type="text"
                              value={shipping.city}
                              onChange={onShip("city")}
                              className={inputClass("shipping.city")}
                            />
                            {getFieldError("shipping.city") && (
                              <p className="mt-1 text-sm text-red-600">
                                {getFieldError("shipping.city")}
                              </p>
                            )}
                          </div>

                          <div>
                            <label className="mb-1 block text-sm font-medium">
                              {t("Código postal")}
                            </label>
                            <input
                              type="text"
                              value={shipping.postal_code}
                              onChange={(e) => {
                                setShipping((prev) => ({
                                  ...prev,
                                  postal_code: formatPostalCode(e.target.value),
                                }));
                                clearFieldError("shipping.postal_code");
                              }}
                              className={inputClass("shipping.postal_code")}
                              placeholder="1234-567"
                              maxLength={8}
                            />
                            {getFieldError("shipping.postal_code") && (
                              <p className="mt-1 text-sm text-red-600">
                                {getFieldError("shipping.postal_code")}
                              </p>
                            )}
                          </div>

                          <div>
                            <label className="mb-1 block text-sm font-medium">
                              {t("País (ISO-2)")}
                            </label>
                            <input
                              type="text"
                              value={shipping.country_code}
                              onChange={onShip("country_code")}
                              className={inputClass("shipping.country_code")}
                              maxLength={2}
                            />
                            {getFieldError("shipping.country_code") && (
                              <p className="mt-1 text-sm text-red-600">
                                {getFieldError("shipping.country_code")}
                              </p>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="mb-1 block text-sm font-medium">
                            {t("Telemóvel")}
                          </label>
                          <input
                            type="tel"
                            value={shipping.phone}
                            onChange={onShip("phone")}
                            className={inputClass("shipping.phone")}
                            placeholder="9xxxxxxxx"
                          />
                          {getFieldError("shipping.phone") && (
                            <p className="mt-1 text-sm text-red-600">
                              {getFieldError("shipping.phone")}
                            </p>
                          )}
                        </div>

                        <label className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={saveAddress}
                            onChange={(e) => setSaveAddress(e.target.checked)}
                          />
                          {t("Guardar esta morada na minha conta")}
                        </label>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">
                  {t("Código de cupão (opcional)")}
                </label>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value.toUpperCase());
                      setCouponResult(null);
                      setCouponFeedback("");
                    }}
                    className="w-full rounded border px-3 py-2 focus:outline-none focus:ring"
                    placeholder={t("EX: WELCOME10")}
                  />

                  <button
                    type="button"
                    onClick={validateCoupon}
                    disabled={couponLoading || !couponCode.trim()}
                    className="shrink-0 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-60"
                  >
                    {couponLoading ? t("A validar...") : t("Aplicar")}
                  </button>
                </div>

                {couponFeedback && (
                  <div
                    className={`mt-2 rounded-md border px-3 py-2 text-sm ${
                      couponResult?.valid
                        ? "border-green-200 bg-green-50 text-green-700"
                        : "border-red-200 bg-red-50 text-red-700"
                    }`}
                  >
                    {couponFeedback}
                  </div>
                )}

                {couponResult?.valid && (
                  <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-3 text-sm">
                    <div className="font-semibold text-green-800">
                      {t("Cupão aplicado")}: {couponResult.coupon.code}
                    </div>

                    <div className="mt-1 text-green-700">
                      {t("Desconto sem IVA")}: €{discountAmount.toFixed(2)}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4 border-t pt-6">
                <div>
                  <h2 className="mb-1 text-lg font-semibold text-gray-900">
                    {t("Dados de faturação")}
                  </h2>
                  <p className="text-xs text-gray-500">
                    {t("O NIF é usado para faturação (opcional).")}
                  </p>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium">
                    {t("NIF")}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={taxNumber}
                    onChange={(e) => {
                      setTaxNumber(
                        e.target.value.replace(/\D/g, "").slice(0, 9)
                      );
                      clearFieldError("billing.tax_number");
                    }}
                    className={inputClass("billing.tax_number")}
                    placeholder="123456789"
                    maxLength={9}
                  />
                  {getFieldError("billing.tax_number") && (
                    <p className="mt-1 text-sm text-red-600">
                      {getFieldError("billing.tax_number")}
                    </p>
                  )}
                </div>

                <label className="flex items-center gap-2 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={billingEnabled}
                    onChange={(e) => setBillingEnabled(e.target.checked)}
                  />
                  {t("Usar uma morada de faturação diferente")}
                </label>

                {billingEnabled && (
                  <div className="space-y-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium">
                        {t("Morada (linha 1)")}
                      </label>
                      <input
                        type="text"
                        value={billing.line1}
                        onChange={onBill("line1")}
                        className={inputClass("billing.line1")}
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div>
                        <label className="mb-1 block text-sm font-medium">
                          {t("Cidade")}
                        </label>
                        <input
                          type="text"
                          value={billing.city}
                          onChange={onBill("city")}
                          className={inputClass("billing.city")}
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-sm font-medium">
                          {t("Código postal")}
                        </label>
                        <input
                          type="text"
                          value={billing.postal_code}
                          onChange={(e) => {
                            setBilling((prev) => ({
                              ...prev,
                              postal_code: formatPostalCode(e.target.value),
                            }));
                            clearFieldError("billing.postal_code");
                          }}
                          className={inputClass("billing.postal_code")}
                          placeholder="1234-567"
                          maxLength={8}
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-sm font-medium">
                          {t("País (ISO-2)")}
                        </label>
                        <input
                          type="text"
                          value={billing.country_code}
                          onChange={onBill("country_code")}
                          className={inputClass("billing.country_code")}
                          maxLength={2}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium">
                        {t("Telemóvel")}
                      </label>
                      <input
                        type="tel"
                        value={billing.phone}
                        onChange={onBill("phone")}
                        className={inputClass("billing.phone")}
                        placeholder="9xxxxxxxx"
                      />
                    </div>
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !items.length || shippingCost === null}
                className="w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-60"
              >
                {loading
                  ? t("A redirecionar para pagamento...")
                  : shippingCost === null
                    ? t("Não foi possível calcular o envio")
                    : t("Ir para pagamento")}
              </button>

              <div className="text-center text-xs text-gray-500">
                {t(
                  "Ao confirmar, vais ser redirecionado para a Stripe para concluir o pagamento."
                )}
              </div>
            </form>
          </div>

          <div className="sticky top-6 h-fit rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-200 p-4">
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  {t("Resumo da encomenda")}
                </div>
                <div className="text-xs text-gray-500">
                  {items.length}{" "}
                  {items.length === 1 ? t("item") : t("itens")}
                </div>
              </div>

              <button
                type="button"
                onClick={() => navigate("/products")}
                className="text-xs font-semibold text-indigo-600 hover:underline"
              >
                {t("Continuar a comprar")}
              </button>
            </div>

            <div className="space-y-4 p-4">
              {items.map((item) => (
                <div key={item.id} className="flex gap-3">
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                        {t("Sem foto")}
                      </div>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="line-clamp-1 text-sm font-semibold text-gray-900">
                      {item.name}
                    </div>

                    {item.variant_label && (
                      <div className="text-xs text-gray-500">
                        {item.variant_type && (
                          <span className="font-medium">
                            {item.variant_type} ·{" "}
                          </span>
                        )}
                        {item.variant_label}
                      </div>
                    )}

                    <div className="mt-0.5 text-xs text-gray-500">
                      €{Number(item.price || 0).toFixed(2)} × {item.quantity}
                    </div>
                  </div>

                  <div className="shrink-0 text-sm font-bold text-gray-900">
                    €
                    {Number(
                      item.line_total ??
                        Number(item.price || 0) * Number(item.quantity || 0)
                    ).toFixed(2)}
                  </div>
                </div>
              ))}

              <div className="space-y-2 border-t border-gray-200 pt-4">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{t("Subtotal sem IVA")}</span>
                  <span>€{subtotalWithoutIva.toFixed(2)}</span>
                </div>

                {discountAmount > 0 && (
                  <div className="flex justify-between text-sm text-green-700">
                    <span>{t("Desconto sem IVA")}</span>
                    <span>-€{discountAmount.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between text-sm text-gray-600">
                  <span>
                    {t("IVA")} ({(ivaRate * 100).toFixed(0)}%)
                  </span>
                  <span>€{ivaAmountAfterDiscount.toFixed(2)}</span>
                </div>

                <div className="flex justify-between text-sm text-gray-600">
                  <span>{t("Subtotal com IVA")}</span>
                  <span>€{subtotalAfterDiscountWithIva.toFixed(2)}</span>
                </div>

                <div className="flex justify-between text-sm text-gray-600">
                  <span>{t("Peso total")}</span>
                  <span>{Number(totalWeightKg).toFixed(2)} kg</span>
                </div>

                <div className="flex justify-between text-sm text-gray-600">
                  <span>{t("Envio")}</span>
                  {shippingCost !== null ? (
                    <span>
                      {shippingCost === 0
                        ? t("Grátis")
                        : `€${Number(shippingCost).toFixed(2)}`}
                    </span>
                  ) : (
                    <span className="text-gray-400">{t("Indisponível")}</span>
                  )}
                </div>

                <div className="flex justify-between pt-2 text-base font-bold text-gray-900">
                  <span>{t("Total estimado")}</span>
                  <span>€{totalAfterDiscount.toFixed(2)}</span>
                </div>

                {shippingCost === null && (
                  <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-600">
                    {t(
                      "Não foi possível calcular o envio com o peso atual do carrinho."
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}